import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../utils/api';
import SectionCard from '../../components/SectionCard';

const COMPARE_MODES = [
  { id: 'period', label: 'Period' },
  { id: 'average', label: 'Average' },
  { id: 'highest', label: 'Highest' },
  { id: 'lowest', label: 'Lowest' },
];

function round1(value) {
  return Math.round(Number(value) * 10) / 10;
}

function formatSigned(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const number = Number(value);
  return `${number > 0 ? '+' : ''}${number.toFixed(1)}°C`;
}

function formatDisplayDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value || '—';
}

function earlierPoints(points) {
  return (points || []).slice(0, -1);
}

function compareValue(points, mode, period) {
  const earlier = earlierPoints(points);
  if (mode === 'period') {
    const match = [...earlier].reverse().find((point) => point.date === period);
    return match ? round1(match.diff) : null;
  }
  const diffs = earlier.map((point) => Number(point.diff)).filter((value) => Number.isFinite(value));
  if (!diffs.length) return null;
  if (mode === 'highest') return round1(Math.max(...diffs));
  if (mode === 'lowest') return round1(Math.min(...diffs));
  return round1(diffs.reduce((sum, value) => sum + value, 0) / diffs.length);
}

function SensorChart({ sensorRows, modeLabel, limit }) {
  const peak = sensorRows.reduce((max, row) => {
    return Math.max(max, Math.abs(Number(row.inspection) || 0), Math.abs(Number(row.compare) || 0));
  }, Math.abs(Number(limit) || 1));
  const bound = Math.max(Math.abs(Number(limit) || 1), Math.ceil(peak * 12) / 10);
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={sensorRows} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
          <YAxis domain={[-bound, bound]} tick={{ fontSize: 11 }} unit="°" width={48} />
          <Tooltip
            formatter={(value, name) => [formatSigned(value), name]}
          />
          <ReferenceLine y={0} stroke="#94a3b8" />
          {limit != null && <ReferenceLine y={limit} stroke="#b91c1c" strokeDasharray="4 4" />}
          {limit != null && <ReferenceLine y={-limit} stroke="#b91c1c" strokeDasharray="4 4" />}
          <Bar dataKey="inspection" name="This inspection" fill="#1d4ed8" />
          <Bar dataKey="compare" name={modeLabel} fill="#15803d" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const SENSOR_FRAMES = [
  { id: 'cal-door', label: 'D1', limit: 1 },
  { id: 'cal-logger', label: 'M1', limit: 0.5 },
];

function graphSensorTitle(group) {
  // Chart title is the sensor code (D1/M1). Bars stay chiller names (building unit).
  const code = group?.code;
  if (code) return code;
  const label = String(group?.label || '');
  const match = label.match(/\b([DM]\d+)\b/i);
  if (match) return match[1].toUpperCase();
  if (group?.id === 'cal-door') return 'D1';
  if (group?.id === 'cal-logger') return 'M1';
  return label || group?.id || 'Sensor';
}

export default function CalibrationGraph({ buildingId, moduleCode, assets: registered = [] }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['building', buildingId, moduleCode, 'calibration-history'],
    queryFn: async () =>
      (await api.get(`/inspection-modules/${moduleCode}/buildings/${buildingId}/calibration-history`)).data?.data,
    enabled: !!buildingId,
  });
  const assets = data?.assets || [];
  const [mode, setMode] = useState('average');
  const pastDates = useMemo(() => {
    const dates = new Set();
    assets.forEach((asset) => {
      (asset.sensors || []).forEach((sensor) => {
        earlierPoints(sensor.points).forEach((point) => {
          if (point.date) dates.add(point.date);
        });
      });
    });
    return [...dates].sort().reverse();
  }, [assets]);
  const [period, setPeriod] = useState('');
  const selectedPeriod = pastDates.includes(period) ? period : pastDates[0] || '';
  const modeLabel = COMPARE_MODES.find((item) => item.id === mode)?.label || 'Average';
  const sensorGroups = useMemo(() => {
    const groups = new Map();
    assets.forEach((asset) => {
      (asset.sensors || []).forEach((sensor) => {
        const latest = sensor.latest || sensor.points?.[sensor.points.length - 1];
        if (!latest || latest.diff == null) return;
        if (!groups.has(sensor.id)) {
          groups.set(sensor.id, {
            id: sensor.id,
            code: sensor.code || null,
            label: sensor.label,
            limit: sensor.limit,
            rows: [],
          });
        }
        const group = groups.get(sensor.id);
        if (group.limit == null && sensor.limit != null) group.limit = sensor.limit;
        if (!group.code && sensor.code) group.code = sensor.code;
        group.rows.push({
          name: asset.name,
          inspection: round1(latest.diff),
          compare: compareValue(sensor.points, mode, selectedPeriod),
        });
      });
    });
    return [...groups.values()];
  }, [assets, mode, selectedPeriod]);
  const charts = useMemo(() => {
    const rooms = (registered || []).map((asset) => asset.name).filter(Boolean);
    const frames = [
      ...SENSOR_FRAMES,
      ...sensorGroups
        .filter((group) => !SENSOR_FRAMES.some((frame) => frame.id === group.id))
        .map((group) => ({
          id: group.id,
          label: graphSensorTitle(group),
          limit: group.limit,
        })),
    ];
    return frames.map((frame) => {
      const group = sensorGroups.find((item) => item.id === frame.id);
      const byName = new Map((group?.rows || []).map((row) => [row.name, row]));
      const names = [...rooms];
      (group?.rows || []).forEach((row) => {
        if (row.name && !names.includes(row.name)) names.push(row.name);
      });
      return {
        id: frame.id,
        label: graphSensorTitle(group || frame),
        limit: group?.limit != null ? group.limit : frame.limit,
        rows: names.map((name) => byName.get(name) || { name, inspection: null, compare: null }),
      };
    });
  }, [registered, sensorGroups]);

  return (
    <div className="space-y-4">
      {isLoading && <p className="text-sm text-gray-500">Loading readings…</p>}
      {isError && (
        <p className="text-sm text-red-700">{error?.response?.data?.error || 'Could not load calibration history.'}</p>
      )}
      <SectionCard
        title="Compare"
        actions={
          <>
            <select
              className="border border-navy-200 rounded px-2 py-1.5 text-sm"
              value={mode}
              onChange={(event) => setMode(event.target.value)}
            >
              {COMPARE_MODES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            {mode === 'period' && !!pastDates.length && (
              <select
                className="border border-navy-200 rounded px-2 py-1.5 text-sm"
                value={selectedPeriod}
                onChange={(event) => setPeriod(event.target.value)}
              >
                {pastDates.map((date) => (
                  <option key={date} value={date}>
                    {formatDisplayDate(date)}
                  </option>
                ))}
              </select>
            )}
          </>
        }
      >
        {charts.map((group) => (
          <div key={group.id} className="space-y-2">
            <p className="text-base font-bold text-gray-900">{group.label}</p>
            <SensorChart sensorRows={group.rows} modeLabel={modeLabel} limit={group.limit} />
          </div>
        ))}
      </SectionCard>
    </div>
  );
}
